import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  FileText, Plus, ChevronRight, Search, ArrowLeft, CheckCircle2,
  TrendingUp, AlertTriangle, Briefcase, FolderKanban, Building2, GitBranch,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate, daysFromNow } from "@/lib/dates";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { formatQuoteNumber } from "./quoteFormat";

// =============================================================================
// Quotes list
// =============================================================================
// Fixes (22 Apr 2026):
//  - KPI cards now have their OWN query so counts/sums don't depend on the
//    current status filter. Previously: filter=draft → only drafts loaded →
//    accepted card always read $0 even when there were accepted quotes.
//  - Default filter is "all" so you see your whole pipeline on first load.
//  - Row onClick uses react-router navigate instead of window.location.href
//    (no page reload, react-query cache preserved).
//  - NewQuoteScreen insert now captures created_by explicitly so new quotes
//    never get orphaned.
// =============================================================================
export function QuotesListScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  // T-244 — added "superseded" filter; default "all" excludes superseded so old revisions don't clutter the active pipeline.
  const [filter, setFilter] = useState<"draft" | "sent" | "accepted" | "all" | "superseded">("all");
  const [search, setSearch] = useState("");
  // James, 22 Sep 2026: "Should have a client filter for quick data filtering"
  // + "clean widgets like AR". Both read off the whole book of quotes below,
  // not the current view, so switching a filter never changes the totals.
  const [clientFilter, setClientFilter] = useState<string>("all");

  const canSeeFinancials = hasRole(user, "manager");

  // Whole-book query — independent of every filter on the screen. Feeds the
  // KPI strip, the by-client widgets and the client dropdown's options.
  const { data: book } = useQuery({
    queryKey: ["quotes_book"],
    queryFn: async () => {
      const [quotesRes, clientsRes] = await Promise.all([
        supabase.from("quotes")
          .select("quote_id, status, total_incl_gst, valid_until, quote_date, client_id")
          .neq("status", "superseded")
          .limit(2000),
        supabase.from("clients").select("client_id, legal_name"),
      ]);
      if (quotesRes.error) throw quotesRes.error;
      const names = new Map((clientsRes.data ?? []).map((c: any) => [c.client_id, c.legal_name]));
      return ((quotesRes.data ?? []) as any[]).map(q => ({
        ...q,
        client_name: names.get(q.client_id) ?? "—",
      }));
    },
  });

  const rowsAll = (book ?? []) as any[];

  const kpi = React.useMemo(() => ({
    total_draft:    rowsAll.filter(q => q.status === "draft").length,
    total_sent:     rowsAll.filter(q => q.status === "sent").length,
    pipeline_value: rowsAll.filter(q => q.status === "sent").reduce((a, q) => a + Number(q.total_incl_gst ?? 0), 0),
    accepted_value: rowsAll.filter(q => q.status === "accepted").reduce((a, q) => a + Number(q.total_incl_gst ?? 0), 0),
  }), [rowsAll]);

  // Client dropdown — only clients that actually have a quote, named.
  const clientOptions = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const q of rowsAll) if (q.client_id) m.set(q.client_id, q.client_name);
    return Array.from(m.entries())
      .map(([client_id, legal_name]) => ({ client_id, legal_name }))
      .sort((a, b) => String(a.legal_name).localeCompare(String(b.legal_name)));
  }, [rowsAll]);

  // Pipeline (quotes SENT and still undecided) split by client — the same
  // shape as the AR "outstanding by client" donut, so the two screens read
  // the same way.
  const byClient = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const q of rowsAll) {
      if (q.status !== "sent") continue;
      const v = Number(q.total_incl_gst ?? 0);
      if (v <= 0) continue;
      m.set(q.client_name, (m.get(q.client_name) ?? 0) + v);
    }
    return Array.from(m.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [rowsAll]);
  const pipelineTotal = React.useMemo(() => byClient.reduce((a, c) => a + c.value, 0), [byClient]);

  // Three numbers under the donut: what is about to lapse, what already has,
  // and what has been won in the last 90 days.
  const ageing = React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const in7   = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    const ago90 = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
    let expiring = 0, lapsed = 0, wonRecent = 0;
    for (const q of rowsAll) {
      const v = Number(q.total_incl_gst ?? 0);
      if (q.status === "sent") {
        if (q.valid_until && q.valid_until < today) lapsed += v;
        else if (q.valid_until && q.valid_until <= in7) expiring += v;
      } else if (q.status === "accepted" && q.quote_date && q.quote_date >= ago90) {
        wonRecent += v;
      }
    }
    return { expiring, lapsed, wonRecent };
  }, [rowsAll]);

  const CLIENT_COLORS = ["#F97316", "#10B981", "#3B82F6", "#A855F7", "#EAB308", "#EF4444", "#14B8A6", "#EC4899", "#6366F1", "#84CC16"];

  // Filtered list. Fetch quotes first (no joins), then hydrate client/project/bid
  // names via a separate query. This is bulletproof against RLS or FK quirks on
  // the joined tables — a failure on those doesn't wipe out the whole list.
  const { data: quotes = [], isLoading, error: listError } = useQuery({
    queryKey: ["quotes_list", filter, search, clientFilter],
    queryFn: async () => {
      let q = supabase
        .from("quotes")
        .select(`
          quote_id, quote_number, quote_title, status, quote_date, valid_until,
          total_incl_gst, margin_percent,
          client_id, project_id, bid_id,
          revision_number, parent_quote_id, quote_type
        `)
        .order("quote_date", { ascending: false, nullsFirst: false })
        .limit(200);
      // T-244 — exclude superseded from default "all" view (still surfaceable via the dedicated filter)
      if (filter === "all") q = q.neq("status", "superseded");
      else q = q.eq("status", filter);
      if (clientFilter !== "all") q = q.eq("client_id", clientFilter);
      if (search.trim()) q = q.or(`quote_number.ilike.%${search}%,quote_title.ilike.%${search}%`);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as any[];
      if (rows.length === 0) return rows;

      // Bulk-hydrate the 3 join targets via IN queries. Each one is independent
      // so a failure on one (e.g. RLS blocks projects) doesn't kill the list.
      const clientIds  = [...new Set(rows.map(r => r.client_id).filter(Boolean))];
      const projectIds = [...new Set(rows.map(r => r.project_id).filter(Boolean))];
      const bidIds     = [...new Set(rows.map(r => r.bid_id).filter(Boolean))];

      const [clientsRes, projectsRes, bidsRes] = await Promise.all([
        clientIds.length  ? supabase.from("clients").select("client_id, legal_name").in("client_id", clientIds)    : Promise.resolve({ data: [] as any[] }),
        projectIds.length ? supabase.from("projects").select("project_id, project_code").in("project_id", projectIds) : Promise.resolve({ data: [] as any[] }),
        bidIds.length     ? supabase.from("bids").select("bid_id, bid_number").in("bid_id", bidIds)               : Promise.resolve({ data: [] as any[] }),
      ]);

      const clientMap  = new Map((clientsRes.data  ?? []).map((c: any) => [c.client_id, c]));
      const projectMap = new Map((projectsRes.data ?? []).map((p: any) => [p.project_id, p]));
      const bidMap     = new Map((bidsRes.data     ?? []).map((b: any) => [b.bid_id, b]));

      return rows.map(r => ({
        ...r,
        client:  clientMap.get(r.client_id)   ?? null,
        project: projectMap.get(r.project_id) ?? null,
        bid:     bidMap.get(r.bid_id)         ?? null,
      }));
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Quotes</h1>
          <div className="text-text-muted text-[13px] mt-1">Build, send, track. Accepted quotes flow into projects + invoices.</div>
        </div>
        {hasRole(user, "manager") && (
          <Link to="/quotes/new"><Button icon={Plus}>New quote</Button></Link>
        )}
      </div>

      {canSeeFinancials && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KPI label="Drafts" value={kpi?.total_draft ?? 0} icon={FileText} />
          <KPI label="Sent" value={kpi?.total_sent ?? 0} />
          <KPI label="Pipeline" value={fmtMoney(kpi?.pipeline_value ?? 0)} sub="sent, undecided" accent={(kpi?.pipeline_value ?? 0) > 0} />
          <KPI label="Accepted" value={fmtMoney(kpi?.accepted_value ?? 0)} icon={TrendingUp} />
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted" />
          <input placeholder="Search quote # or title..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1" />
        </div>
        <div className="flex items-center gap-2">
          {(["draft", "sent", "accepted", "all", "superseded"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
                filter === f ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line hover:text-text"
              }`}>{f}</button>
          ))}
        </div>
        <select
          value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
          className="bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
        >
          <option value="all">All clients</option>
          {clientOptions.map(c => (
            <option key={c.client_id} value={c.client_id}>{c.legal_name}</option>
          ))}
        </select>
      </div>

      {canSeeFinancials && byClient.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
          <Card>
            <div className="p-4">
              <div className="text-[13px] font-medium">Pipeline by client</div>
              <div className="text-[11px] text-text-muted mb-1">
                {fmtMoney(pipelineTotal)} out with clients, undecided
              </div>
              <div style={{ width: "100%", height: 190 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={byClient} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={1}>
                      {byClient.map((_, i) => <Cell key={i} fill={CLIENT_COLORS[i % CLIENT_COLORS.length]} />)}
                    </Pie>
                    <RTooltip formatter={(val: any) => fmtMoney(Number(val))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
          <Card className="lg:col-span-2">
            <div className="p-4">
              <div className="text-[13px] font-medium mb-2">Out with each client</div>
              <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                {byClient.map((c, i) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => {
                      const hit = clientOptions.find(o => o.legal_name === c.name);
                      if (hit) { setClientFilter(hit.client_id); setFilter("sent"); }
                    }}
                    className="w-full flex items-center gap-2 text-[12px] hover:bg-ink-hover rounded px-1 -mx-1 py-0.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: CLIENT_COLORS[i % CLIENT_COLORS.length] }} />
                    <span className="text-text flex-1 truncate text-left">{c.name}</span>
                    <span className="font-mono text-text-muted w-10 text-right">{pipelineTotal > 0 ? Math.round((c.value / pipelineTotal) * 100) : 0}%</span>
                    <span className="font-mono text-text w-24 text-right">{fmtMoney(c.value)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-line grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Expiring ≤7d</div>
                  <div className="font-mono text-[13px] text-warn">{fmtMoney(ageing.expiring)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Past valid-until</div>
                  <div className="font-mono text-[13px] text-danger">{fmtMoney(ageing.lapsed)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Won, last 90d</div>
                  <div className="font-mono text-[13px] text-ok">{fmtMoney(ageing.wonRecent)}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {listError && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5" />
          Quote list failed to load: {(listError as any)?.message ?? String(listError)}
        </div>
      )}

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div>
       : quotes.length === 0 ? <Card><EmptyState title="No quotes in this view" description="Click 'New quote' to create one." icon={FileText}/></Card>
       : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {[
                    "Quote #", "Title", "Client", "Source", "Date", "Valid",
                    ...(canSeeFinancials ? ["Total", "Margin"] : []),
                    "Status", "",
                  ].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {quotes.map((q, i) => {
                    const expiring = q.valid_until != null ? daysFromNow(q.valid_until) : null;
                    return (
                      <tr key={q.quote_id} className={`border-b border-line cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}
                        onClick={() => navigate(`/quotes/${q.quote_id}`)}>
                        <td className="px-4 py-3 font-mono text-[11px] text-accent">{formatQuoteNumber(q)}</td>
                        <td className="px-4 py-3 max-w-[280px] truncate">{q.quote_title ?? "—"}</td>
                        <td className="px-4 py-3 text-[12px]">{q.client?.legal_name ?? "—"}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-text-muted">
                          {q.project?.project_code ?? q.bid?.bid_number ?? <span className="text-text-dim">standalone</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{fmtDate(q.quote_date)}</td>
                        <td className="px-4 py-3 font-mono text-[12px]">
                          {fmtDate(q.valid_until)}
                          {q.status === "sent" && expiring != null && expiring < 7 && (
                            <AlertTriangle size={11} className="inline ml-1 text-warn" />
                          )}
                        </td>
                        {canSeeFinancials && (
                          <>
                            <td className="px-4 py-3 font-mono">{fmtMoney(q.total_incl_gst)}</td>
                            <td className="px-4 py-3 font-mono text-[12px]">
                              <span className={
                                q.margin_percent == null ? "text-text-dim" :
                                q.margin_percent >= 30 ? "text-ok" :
                                q.margin_percent >= 15 ? "text-warn" :
                                "text-danger"
                              }>
                                {q.margin_percent != null ? `${Number(q.margin_percent).toFixed(0)}%` : "—"}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3"><Pill label={q.status} tone={statusTone(q.status)} small /></td>
                        <td className="px-4 py-3"><ChevronRight size={14} className="text-text-dim"/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}

function statusTone(s: string): any {
  switch (s) {
    case "draft":             return "pending";
    case "sent":              return "mobilising";
    case "pending_approval":  return "warning";
    case "accepted":          return "approved";
    case "rejected":          return "rejected";
    case "withdrawn":         return "default";
    case "expired":           return "default";
    case "superseded":        return "default";   // T-244 — old version replaced by a newer revision
    default:                  return "default";
  }
}


// =============================================================================
// New quote picker — three paths
// =============================================================================
type NewQuoteMode = "bid" | "project" | "standalone" | "variation";

export function NewQuoteScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();

  // 2 Nov 2026 — James: "create quote or variation in the project so no need to
  // go Finance > Quotes > New quote and select the project". The project page
  // links here as /quotes/new?project=<id>&mode=project|variation; the mode and
  // project are preset and the client / site follow (the effect below).
  const [qs] = useSearchParams();
  const presetProject = qs.get("project") ?? "";
  const presetMode = (qs.get("mode") === "variation" ? "variation" : presetProject ? "project" : "bid") as NewQuoteMode;
  const [mode, setMode] = useState<NewQuoteMode>(presetMode);
  const [bid_id, setBidId] = useState("");
  const [project_id, setProjectId] = useState(presetProject);
  const [client_id, setClientId] = useState("");
  const [site_id, setSiteId] = useState("");
  const [quote_title, setQuoteTitle] = useState("");
  const [parent_quote_id, setParentQuoteId] = useState("");   // T-190 — for mode='variation'
  // mig 454 — Fixed price (the builder as it has always been) or Schedule of
  // rates (priced on the client's own rate card). Standard quotes only.
  const [pricing_basis, setPricingBasis] = useState<"fixed_price" | "schedule_of_rates">("fixed_price");
  const [client_card_id, setClientCardId] = useState("");
  // mig 455 — a standalone quote with no site record: type the site and the client's details
  const [site_name_text, setSiteNameText] = useState("");
  const [site_address_text, setSiteAddressText] = useState("");
  const [client_reference, setClientReference] = useState("");

  const { data: openBids = [] } = useQuery({
    queryKey: ["bids_quotable"],
    enabled: mode === "bid",
    queryFn: async () => (await supabase
      .from("bids")
      .select(`
        bid_id, bid_number, bid_title, client_id, site_id,
        client:clients(legal_name), site:sites(site_code, site_name)
      `)
      .in("status", ["open", "responded", "won"])
      .order("received_date", { ascending: false })
      .limit(100)).data ?? [],
  });

  const { data: activeProjects = [] } = useQuery({
    queryKey: ["projects_quotable"],
    enabled: mode === "project" || mode === "variation",
    queryFn: async () => (await supabase
      .from("projects")
      .select(`
        project_id, project_code, project_name, client_id, site_id,
        client:clients(legal_name), site:sites(site_code, site_name)
      `)
      .in("status", ["scheduled", "mobilising", "live", "on_hold", "acceptance_pending"])
      .order("project_code", { ascending: false })
      .limit(100)).data ?? [],
  });

  // T-190 — variations: parent quote picker, scoped to the selected project,
  // standard quotes (not other variations) in sent/accepted status.
  const { data: parentQuotes = [] } = useQuery({
    queryKey: ["parent_quotes_for_variation", project_id],
    enabled: mode === "variation" && !!project_id,
    queryFn: async () => (await supabase
      .from("quotes")
      .select("quote_id, quote_number, quote_title, status, total_incl_gst")
      .eq("project_id", project_id)
      .eq("quote_type", "standard")
      .in("status", ["sent", "accepted"])
      .order("quote_number")).data ?? [],
  });

  const { data: clients = [] } = useQuery({
    queryKey: ["clients_active"],
    enabled: mode === "standalone",
    queryFn: async () => (await supabase
      .from("clients")
      .select("client_id, client_code, legal_name")
      .eq("is_active", true)
      .order("legal_name")).data ?? [],
  });

  const { data: sites = [] } = useQuery({
    queryKey: ["sites_all"],
    enabled: mode === "standalone",
    queryFn: async () => (await supabase
      .from("sites")
      .select("site_id, site_code, site_name")
      .order("site_code").limit(1000)).data ?? [],
  });

  // mig 454 — the client's schedule-of-rates cards, for the SoR basis
  const { data: clientCards = [] } = useQuery({
    queryKey: ["client_sor_cards", client_id],
    enabled: !!client_id && pricing_basis === "schedule_of_rates",
    queryFn: async () => {
      const { data } = await (supabase as any).from("rate_cards")
        .select("rate_card_id, display_name, version_label, is_active")
        .eq("client_id", client_id).eq("rate_card_type", "client_sor").eq("is_active", true).order("created_at", { ascending: false });
      return data ?? [];
    },
  });
  React.useEffect(() => {
    if (pricing_basis === "schedule_of_rates" && !client_card_id && (clientCards as any[]).length) setClientCardId((clientCards as any[])[0].rate_card_id);
  }, [clientCards, pricing_basis]);

  const { data: defaultRateCardId } = useQuery({
    queryKey: ["default_rate_card"],
    queryFn: async () => {
      const { data } = await supabase
        .from("rate_cards")
        .select("rate_card_id")
        .eq("rate_card_type", "coretel_sor")
        .eq("is_default", true)
        .eq("is_active", true)
        .maybeSingle();
      return data?.rate_card_id ?? null;
    },
  });

  React.useEffect(() => {
    if (mode !== "bid" || !bid_id) return;
    const b = (openBids as any[]).find(x => x.bid_id === bid_id);
    if (b) {
      setClientId(b.client_id ?? "");
      setSiteId(b.site_id ?? "");
      if (!quote_title) setQuoteTitle(b.bid_title ?? "");
    }
  }, [bid_id, openBids, mode]);

  React.useEffect(() => {
    if ((mode !== "project" && mode !== "variation") || !project_id) return;
    const p = (activeProjects as any[]).find(x => x.project_id === project_id);
    if (p) {
      setClientId(p.client_id ?? "");
      setSiteId(p.site_id ?? "");
      if (!quote_title && mode === "project") setQuoteTitle(p.project_name ?? "");
    }
  }, [project_id, activeProjects, mode]);

  // T-190 — variation mode: when parent quote picked, auto-suggest the title
  // as "VAR — {parent title}" so the manager doesn't retype it.
  React.useEffect(() => {
    if (mode !== "variation" || !parent_quote_id) return;
    const parent = (parentQuotes as any[]).find(q => q.quote_id === parent_quote_id);
    if (parent && !quote_title) {
      setQuoteTitle(`VAR — ${parent.quote_title ?? ""}`.trim());
    }
  }, [parent_quote_id, parentQuotes, mode]);

  React.useEffect(() => {
    setBidId(""); setProjectId(""); setClientId(""); setSiteId(""); setQuoteTitle("");
    setParentQuoteId("");   // T-190
  }, [mode]);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!client_id) throw new Error("Client must be selected (picked automatically when you choose a BID or Project).");
      if (!quote_title.trim()) throw new Error("Quote title is required.");
      if (mode === "variation" && !parent_quote_id) throw new Error("Pick the parent quote you're varying.");
      if (mode === "variation" && !project_id)      throw new Error("Variations must be linked to a project.");

      const today = new Date().toISOString().slice(0, 10);
      const validUntil = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

      // Capture the auth user id explicitly so created_by/updated_by don't
      // end up null — defensive against a broken trigger-default pattern.
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userId = authUser?.id ?? null;

      const { data, error } = await (supabase as any)   // mig 454 — pricing_basis is newer than the generated types
        .from("quotes")
        .insert({
          client_id,
          bid_id:       mode === "bid"     ? bid_id     : null,
          project_id:   mode === "project" || mode === "variation" ? project_id : null,
          site_id:      site_id || null,
          rate_card_id: pricing_basis === "schedule_of_rates" ? (client_card_id || null) : (defaultRateCardId ?? null),
          pricing_basis: mode === "variation" ? "fixed_price" : pricing_basis,   // mig 454
          // mig 455 — typed site / reference for a standalone quote with no site record
          site_name_text:    mode === "standalone" && !site_id ? (site_name_text.trim() || null) : null,
          site_address_text: mode === "standalone" && !site_id ? (site_address_text.trim() || null) : null,
          client_reference:  mode === "standalone" ? (client_reference.trim() || null) : null,
          quote_title:  quote_title.trim(),
          status:       "draft",
          quote_date:   today,
          valid_until:  validUntil,
          payment_terms_days: 30,
          // T-190 — variation fields. The DB trigger rewrites quote_number
          // to VAR-YY-NNNN automatically when quote_type='variation'.
          quote_type:        mode === "variation" ? "variation" : "standard",
          parent_quote_id:   mode === "variation" ? parent_quote_id : null,
          created_by:   userId,
          updated_by:   userId,
        })
        .select("quote_id")
        .single();

      if (error) throw error;
      // mig 454 — an SoR quote has no Coretel sections; its lines group by the client's own headings
      if (pricing_basis !== "schedule_of_rates" || mode === "variation") {
        await supabase.schema('app').rpc("seed_quote_sections", { p_quote_id: data.quote_id });
      }
      return data.quote_id;
    },
    onSuccess: (id) => navigate(`/quotes/${id}`),
  });

  const canSubmit = client_id && quote_title.trim() && !createMut.isPending
    && (mode !== "variation" || (parent_quote_id && project_id));

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6 pb-32">
      <button onClick={() => navigate("/quotes")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Quotes
      </button>

      <h1 className="text-2xl font-semibold tracking-tight mb-1">New quote</h1>
      <div className="text-text-muted text-[13px] mb-5">
        What are you quoting against? Pick a source below — client and site get filled in for you.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <ModeTab icon={Briefcase}    label="From a BID"     active={mode === "bid"}        onClick={() => setMode("bid")} />
        <ModeTab icon={FolderKanban} label="From a Project" active={mode === "project"}    onClick={() => setMode("project")} />
        <ModeTab icon={Building2}    label="Standalone"     active={mode === "standalone"} onClick={() => setMode("standalone")} />
        <ModeTab icon={GitBranch}    label="Variation"      active={mode === "variation"}  onClick={() => setMode("variation")} />
      </div>

      <Card>
        <div className="p-5 space-y-4">
          {mode === "bid" && (
            <>
              <Field label="BID *" hint="Only open / responded / won bids appear here.">
                <Select value={bid_id} onChange={e => setBidId(e.target.value)}>
                  <option value="">— select BID —</option>
                  {(openBids as any[]).map(b => (
                    <option key={b.bid_id} value={b.bid_id}>
                      {b.bid_number} · {b.client?.legal_name ?? "—"} · {b.bid_title}
                    </option>
                  ))}
                </Select>
              </Field>
              {bid_id && (
                <AutoFilledPreview
                  client={(openBids as any[]).find(b => b.bid_id === bid_id)?.client?.legal_name}
                  site={(openBids as any[]).find(b => b.bid_id === bid_id)?.site}
                />
              )}
            </>
          )}

          {mode === "project" && (
            <>
              <Field label="Project *" hint="Scheduled, mobilising, live, on-hold, or acceptance-pending projects.">
                <Select value={project_id} onChange={e => setProjectId(e.target.value)}>
                  <option value="">— select project —</option>
                  {(activeProjects as any[]).map(p => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.project_code} · {p.project_name}
                    </option>
                  ))}
                </Select>
              </Field>
              {project_id && (
                <AutoFilledPreview
                  client={(activeProjects as any[]).find(p => p.project_id === project_id)?.client?.legal_name}
                  site={(activeProjects as any[]).find(p => p.project_id === project_id)?.site}
                />
              )}
            </>
          )}

          {mode === "standalone" && (
            <>
              <Field label="Client *" hint="For tenders or one-off quotes not tied to an existing BID or Project.">
                <Select value={client_id} onChange={e => setClientId(e.target.value)}>
                  <option value="">— select client —</option>
                  {(clients as any[]).map(c => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.client_code} · {c.legal_name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Site (optional)" hint="Pick it from the register, or type it below if it isn't in there yet — the typed name goes on the quote and the PDF.">
                <Select value={site_id} onChange={e => setSiteId(e.target.value)}>
                  <option value="">— not in the register / type it below —</option>
                  {(sites as any[]).map(s => (
                    <option key={s.site_id} value={s.site_id}>
                      {s.site_name}{s.site_code ? ` · ${s.site_code}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              {!site_id && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Site name (typed)"><Input value={site_name_text} onChange={e => setSiteNameText(e.target.value)} /></Field>
                  <Field label="Site address (typed)"><Input value={site_address_text} onChange={e => setSiteAddressText(e.target.value)} /></Field>
                </div>
              )}
              <Field label="Client's reference (optional)" hint="Their RFQ / SMR / PO number — printed on the PDF header.">
                <Input mono value={client_reference} onChange={e => setClientReference(e.target.value)} />
              </Field>
            </>
          )}

          {mode === "variation" && (
            <>
              <Field label="Project *" hint="Variations attach to an existing project. Pick the project the original quote is on.">
                <Select value={project_id} onChange={e => setProjectId(e.target.value)}>
                  <option value="">— select project —</option>
                  {(activeProjects as any[]).map(p => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.project_code} · {p.project_name}
                    </option>
                  ))}
                </Select>
              </Field>

              {project_id && (
                <Field label="Variation to which quote? *" hint="Only sent or accepted standard quotes on this project. The new VAR will receive a VAR-YY-NNNN number.">
                  <Select value={parent_quote_id} onChange={e => setParentQuoteId(e.target.value)}>
                    <option value="">— pick the parent quote —</option>
                    {(parentQuotes as any[]).map(q => (
                      <option key={q.quote_id} value={q.quote_id}>
                        {q.quote_number} · {q.quote_title} · {q.status} · {q.total_incl_gst != null ? fmtMoney(q.total_incl_gst) : "—"}
                      </option>
                    ))}
                  </Select>
                  {project_id && (parentQuotes as any[]).length === 0 && (
                    <div className="text-[11px] text-text-muted mt-1">
                      No sent or accepted quotes on this project to variate against. Send/accept the original quote first.
                    </div>
                  )}
                </Field>
              )}

              {project_id && (
                <AutoFilledPreview
                  client={(activeProjects as any[]).find(p => p.project_id === project_id)?.client?.legal_name}
                  site={(activeProjects as any[]).find(p => p.project_id === project_id)?.site}
                />
              )}

              <div className="rounded border border-amber-400/30 bg-amber-400/[0.05] px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2">
                <GitBranch size={12} className="mt-0.5 shrink-0"/>
                <div>
                  <strong>Heads up:</strong> The variation will be created as a draft VAR-YY-NNNN quote linked to the parent. Project total will include this variation once you mark it accepted.
                </div>
              </div>
            </>
          )}

          {mode !== "variation" && (
            <Field label="How is it priced?" hint={pricing_basis === "schedule_of_rates" ? "Against the client's own schedule of rates — their codes, their rates, plus Non-SoR / PSUM items. Load their workbook inside the quote if there's no card yet." : "Your own lines and sections, the builder as it has always worked."}>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setPricingBasis("fixed_price")}
                  className={`px-3 py-3 rounded border text-[12px] font-medium transition-colors ${pricing_basis === "fixed_price" ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line hover:text-text"}`}>Fixed price</button>
                <button type="button" onClick={() => setPricingBasis("schedule_of_rates")}
                  className={`px-3 py-3 rounded border text-[12px] font-medium transition-colors ${pricing_basis === "schedule_of_rates" ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line hover:text-text"}`}>Schedule of rates</button>
              </div>
              {pricing_basis === "schedule_of_rates" && client_id && (
                <div className="mt-2">
                  <Select value={client_card_id} onChange={e => setClientCardId(e.target.value)}>
                    <option value="">— pick the client's rate card later —</option>
                    {(clientCards as any[]).map(c => <option key={c.rate_card_id} value={c.rate_card_id}>{c.display_name}{c.version_label ? ` · ${c.version_label}` : ""}</option>)}
                  </Select>
                  {(clientCards as any[]).length === 0 && <div className="text-[11px] text-text-muted mt-1">No schedule of rates loaded for this client yet — you'll load their workbook on the quote.</div>}
                </div>
              )}
            </Field>
          )}

          <Field label="Quote title *" hint="Shown on the PDF header. Edit freely — we pre-fill from BID/Project title (or 'VAR — {parent title}' for variations).">
            <Input
              value={quote_title}
              onChange={e => setQuoteTitle(e.target.value)}
              placeholder={mode === "variation" ? "e.g. VAR — Andergrove site issue" : site_name_text ? `e.g. ${site_name_text} — PSUM` : "e.g. NBN upgrade — Toowoomba Rooftop"}
            />
          </Field>
        </div>
      </Card>

      {createMut.isError && (
        <div className="mt-3 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5"/>
          <div>{(createMut.error as Error)?.message ?? "Create failed — check console."}</div>
        </div>
      )}

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-2xl w-full">
          <Button variant="secondary" size="lg" onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            size="lg"
            icon={CheckCircle2}
            onClick={() => createMut.mutate()}
            disabled={!canSubmit}
            className="flex-1 justify-center"
          >
            {createMut.isPending ? "Creating…" : "Create & build"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}

function ModeTab({ icon: Icon, label, active, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 justify-center px-3 py-3 rounded border transition-colors text-[12px] font-medium ${
        active
          ? "bg-accent/12 text-accent border-accent/30"
          : "bg-ink-raised text-text-muted border-line hover:text-text"
      }`}
    >
      <Icon size={14}/> {label}
    </button>
  );
}

function AutoFilledPreview({ client, site }: { client?: string; site?: any }) {
  return (
    <div className="bg-ink-raised border border-line rounded p-3 text-[12px] space-y-1">
      <div><span className="text-text-muted">Client:</span> <span className="text-text">{client ?? "—"}</span></div>
      <div>
        <span className="text-text-muted">Site:</span>{" "}
        <span className="text-text">
          {site ? `${site.site_name}${site.site_code ? ` · ${site.site_code}` : ""}` : <span className="text-text-dim">not set</span>}
        </span>
      </div>
    </div>
  );
}
